const Nano = require('./Nano')

const LOCK = '$__lock__$'

function maybePromise(
  mbPromise, callback, asyncOnly, syncOnly
) {
  if (mbPromise && typeof mbPromise.then === 'function') {
    if (typeof asyncOnly === 'function') {
      asyncOnly(mbPromise)
    }

    return mbPromise.then(callback)
  }

  if (typeof syncOnly === 'function') {
    syncOnly(mbPromise)
  }

  return callback(mbPromise)
}
/**
 * Micro
 *
 * Расширение Nano, добавляет методы once, onceIt, evergreen
 * Позволяет обрабатывать асинхронные запросы, использует lock
 *
 * Вы можете использовать этот класс для создания собственного кэшера
 * Используйте clearInterval(this._interval) в constructor, чтобы отключить
 * встроенную систему автоочистки кэша по ttl
 *
 * @class Micro
 * @extends {Nano}
 */
class Micro extends Nano {
  constructor(options = {}) {
    super(options)
  }

  _expireKey(key, element) {
    if (element && typeof element.updater === 'function') {
      const result = element.updater(key)

      if (result !== undefined) {
        return maybePromise(result, result => this.set(key, result, { updater: element.updater }))
      }
    }
    return super._expireKey(key)
  }

  _toCacheValue(value, options = {}) {
    const ttl = options.ttl || this._options.ttl
    const updater = typeof options.updater === 'function' ? options.updater : undefined

    return {
      value,
      expireAt: Date.now() + ttl,
      ttl,
      updater
    }
  }

  add(key, value, options = {}) {
    const result = super.add(key, value, options)

    if (result && typeof result.then === 'function') {
      return result.then(result => {
        if (result === undefined) {
          return this.set(key, value, options)
        }
        return result
      })
    }

    return result
  }

  get(key) {
    let result = this._mapGet(key)

    if (result === undefined) {
      return undefined
    }

    if (result === LOCK) {
      return async function lockAwaiter() {
        let limit = 30 * 1000
        const step = 100

        while ((result = this._mapGet(key)) === LOCK) {
          await new Promise(resolve => setTimeout(resolve, step))
          if ((limit = limit - step) < 0) {
            break
          }
        }

        if (result === undefined) {
          return undefined
        }

        if (result === LOCK) {
          this.delete(key)
          return undefined
        }

        return this._fromCacheValue(result)
      }.call(this)
    } else if (this._options.renewOnGet) {
      result.ttl = Date.now() + this._options.ttl
    }

    return this._fromCacheValue(result)
  }

  once(key, callback, evergreen = false) {
    const getAndSave = () => {
      const updater = evergreen ? callback : undefined
      const result = callback()

      return maybePromise(result,
        result => {
          this.set(key, result, { updater })
          return result
        },
        () => this._mapSet(key, LOCK))
    }

    const result = this.get(key)
    return maybePromise(result, result => {
      if (result === undefined) {
        return getAndSave()
      }
      return result
    })
  }

  onceIt(callback, evergreen = false) {
    const self = this
    return function(key, ...args) {
      return self.once(key, () => callback.call(this, ...args), evergreen)
    }
  }

  evergreen(callback, options = {}) {
    const cache = this
    async function evergreenHandler(id, ...args) {
      const key = '' + (options.prefix || '') + id
      return cache.once(key, () => callback.call(this, id, ...args), true)
    }

    evergreenHandler.update = id => {
      if (!id) {
        return
      }

      const key = '' + (options.prefix || '') + id

      if (!this.has(key)) {
        return
      }

      const cached = this._mapGet(key)

      if (!cached.updater) {
        return
      }

      const result = cached.updater()

      return maybePromise(result, result => {
        this.set(key, result, { updater: cached.updater })
        return result
      })
    }

    return evergreenHandler
  }

  static onceIt(callback, options = {}) {
    const cache = new Micro(options)
    return cache.onceIt(callback)
  }

  static evergreen(callback, options = {}) {
    const cache = new Micro(options)
    return cache.evergreen(callback, options)
  }
}

module.exports = Micro
